// The public password-reset calls, in one place.
//
// These pages are reached signed OUT, so they deliberately do not use the
// authenticated axios client (its 401 interceptor would try to refresh a
// session that does not exist). They did use bare relative fetch("/user/..."),
// which ignores REACT_APP_SERVER_URL: in development that went through the CRA
// proxy to PRODUCTION, and any build whose API lives on another host would
// post to the web host instead. They now resolve the same base URL as every
// other dashboard call.
//
// Every helper resolves to { ok, status, data } and never throws: a network
// failure is status 0, and a non-JSON body (a proxy 502 page, a rate-limit
// page) becomes {} rather than a JSON parse crash that leaves the button
// spinning with no message.
import { dashboardApiBaseUrl } from "./index.js";

const call = async (method, path, body) => {
  try {
    const response = await fetch(`${dashboardApiBaseUrl()}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    let data = {};
    try {
      data = await response.json();
    } catch (_) {
      data = {};
    }
    return { ok: response.ok, status: response.status, data: data || {} };
  } catch (_) {
    return { ok: false, status: 0, data: {} };
  }
};

/** Ask for a reset email. The answer never says whether the account exists. */
export const requestPasswordResetEmail = (email) =>
  call("POST", "/user/post-reset-email", { email });

/** Read-only check of a set-password link before showing the form. */
export const previewResetLink = ({ userId, resetToken }) =>
  call("POST", "/user/reset-password-preview", { userId, resetToken });

/** Redeem a set-password link. `endpointBase` differs for 3PL staff. */
export const redeemResetLink = ({ endpointBase, userId, resetToken, password }) =>
  call("PUT", `${endpointBase}/${userId}`, { password, resetToken });

/** Human copy for a failed reset-email request, by HTTP status. */
export const resetRequestErrorMessage = ({ status, data }) => {
  if (status === 0) {
    return "We could not reach Lusuku. Check your internet connection and try again.";
  }
  if (status === 429) {
    return data?.message || "Too many tries. Please wait 15 minutes and try again.";
  }
  if (data?.message) return data.message;
  return "Something went wrong on our side. Please try again in a few minutes.";
};

export const SUPPORT_EMAIL = "support@lusuku.ug";
export const SUPPORT_WHATSAPP_URL = "https://wa.me/256785010102";
export const SUPPORT_WHATSAPP_DISPLAY = "+256 785 010102";
